import { React, useEffect, useState, Fragment, useContext, createContext, useMemo } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import {
  Paper, Grid, Typography, TextField, MenuItem, Zoom, Table, TableBody, TableHead, TableContainer, TableCell, TableRow,
  Fab, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress, Checkbox
} from '@material-ui/core/';
import { DatePicker } from '@material-ui/pickers';
import AddIcon from '@material-ui/icons/Add';
import { authFetch, logout } from '../../auth';
import DataGrid from 'react-data-grid';

import DoneIcon from '@material-ui/icons/CheckCircleOutline';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.secondary.main,
    padding: 3
  },
  body: {
    fontSize: 14,
    color: theme.palette.primary.dark,
    padding: 5
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(TableRow);

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(2)
  },
  darkText: {
    color: theme.palette.primary.main
  },
  sectionTitle: {
    marginBottom: 10,
    backgroundColor: theme.palette.secondary.light,
    borderRadius: 3,
    color: theme.palette.primary.dark
  },
  componentTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.secondary.main,
    padding: 2,
    borderRadius: 10,
    marginBottom: theme.spacing(1)
  },
  filterInput: {
    fontSize: 14,
    fontFamily: 'Asap Condensed',
    padding: 1,
    width: 200
  },
  filterInput100: {
    fontSize: 14,
    fontFamily: 'Asap Condensed',
    padding: 1,
    maxWidth: 100
  },
  formPaper: {
    padding: theme.spacing(3),
    color: theme.palette.primary.main,
    "> * + *": {
      marginTop: theme.spacing(5)
    }
  },
  formRow: {
    width: "100%",
    maxHeight: "50%",
  },
  labelText: {
    color: theme.palette.primary.main,
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 16
  },
  dialogContent: {
    fontSize: 14,
    color: theme.palette.primary.main,
  },
  dialogTitle: {
    fontSize: 14,
    color: theme.palette.primary.main,
    fontWeight: 'normal'
  },
  formText: {
    fontSize: 14,
    color: theme.palette.primary.main,
    textAlign: "center",
    marginLeft: theme.spacing(1)
  },
  formDefined: {
    backgroundColor: theme.palette.secondary.light,
  },
  formItem: {
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: 'normal',
    color: theme.palette.primary.dark
  },
  formDateText: {
    fontSize: 14,
    color: theme.palette.primary.main,
    textAlign: "left",
  },
  formItemNoPadding: {
    padding: 0
  },
  uploadButton: {
    fontSize: 14,
    [theme.breakpoints.down('xs')]: {
      fontSize: 8,
    },
  }
}));

export default function Summary(props) {
  const classes = useStyles();
  let billingDate = props.billingDate;
  const [feedbackDialog, setFeedbackDialog] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState("");

  const [cycles, setCycles] = useState([]);
  const [cycleUsers, setCycleUsers] = useState([]);
  const [cycleLiqConsumption, setCycleLiqConsumption] = useState({});
  const [cycleLiqAgreement, setCycleLiqAgreement] = useState({});
  const [cycleBillParams, setCycleBillParams] = useState({});
  const [cycleBillGenerated, setCycleBillGenerated] = useState({});
  const [selectedCycle, setSelectedCycle] = useState("");
  const [generationInProgress, setGenerationInProgress] = useState(false);
  const [generationMsg, setGenerationMsg] = useState("");
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const [cycleLiqConsumptionFlag, setCycleLiqConsumptionFlag] = useState(false);
  const [cycleLiqAgreementFlag, setCycleLiqAgreementFlag] = useState(false);
  const [cycleBillParamsFlag, setCycleBillParamsFlag] = useState(false);
  const [cycleBillGeneratedFlag, setCycleBillGeneratedFlag] = useState(false);

  const [cycleHes, setCycleHes] = useState({});
  const [cycleHesFlag, setCycleHesFlag] = useState(false);

  const [cycleBillGeneratedWithHes, setCycleBillGeneratedWithHes] = useState({});
  const [cycleBillGeneratedWithHesFlag, setCycleBillGeneratedWithHesFlag] = useState(false);

  const [generationTypeInProgress, setGenerationTypeInProgress] = useState("");
  const [downloadTypeInProgress, setDownloadTypeInProgress] = useState("");

  const [dianValidating, setDianValidating] = useState(false);
  const [dianIssuing, setDianIssuing] = useState(false);

  // Estados separados para los dos flujos de envío
  const [dianTriggering, setDianTriggering] = useState(false);
  const [emailProcessing, setEmailProcessing] = useState(false);

  const handleFeedbackDialogOpen = () => {
    setFeedbackDialog(true);
  };

  const handleFeedbackDialogClose = () => {
    setFeedbackDialog(false);
  };

  const handleDianValidation = () => {
    setDianValidating(true);
    const isTestEnv = true;

    authFetch(
      `/api/run-dian-validation?username=${String(props.username)}` +
      `&year=${billingDate.getFullYear()}&month=${String(billingDate.getMonth() + 1)}` +
      `&cycle=${selectedCycle}&version=${props.tariffCalcVersion}&test=${isTestEnv}`
    )
      .then(async (response) => {
        if (response.status === 422) {
          logout();
          return null;
        }

        // 1. Parsear el body primero (evita llamar .json() sin await)
        const data = await response.json().catch(() => ({}));

        // 2. Si el código HTTP no es 200/2xx
        if (!response.ok) {
          const errorDetail = data?.error || data?.message || `Código HTTP ${response.status}`;
          handleFeedbackDialogOpen();
          setFeedbackMsg(`Error al ejecutar las validaciones con la DIAN: ${errorDetail}`);
          return null;
        }

        return data;
      })
      .then((data) => {
        if (!data) return; // Si ya se manejó el error en el bloque anterior

        handleFeedbackDialogOpen();
        if (data.status === "OK") {
          setFeedbackMsg(data.message || "Validación ejecutada correctamente.");
        } else {
          const errorDetail = data.error || data.message || "Respuesta no esperada del servidor.";
          setFeedbackMsg(`Error en la estructura de datos DIAN: ${errorDetail}`);
        }
      })
      .catch((err) => {
        handleFeedbackDialogOpen();
        setFeedbackMsg("Error de conexión al validar con la DIAN.");
      })
      .finally(() => {
        // Garantiza apagar el spinner siempre
        setDianValidating(false);
      });
  };
    // Endpoint 1: Disparo de correos DIAN hacia Outlook
  const handleTriggerDianEmails = () => {
    const confirmTrigger = window.confirm(`¿Desea solicitar el envío de correos DIAN a la cuenta de Outlook para el ciclo ${selectedCycle}?`);
    if (!confirmTrigger) return;

    setDianTriggering(true);

    authFetch(`/api/trigger-dian-emails?username=${String(props.username)}` +
      `&year=${billingDate.getFullYear()}&month=${String(billingDate.getMonth() + 1)}` +
      `&cycle=${selectedCycle}&version=${props.tariffCalcVersion}`)
      .then(response => {
        if (response.status === 422) { logout(); return null; }
        else if (response.status !== 200) {
          return response.json().then(err => { throw new Error(err.error || "Error al disparar correos a Outlook"); });
        }
        return response.json();
      })
      .then(data => {
        setDianTriggering(false);
        if (data && data.status === "OK") {
          handleFeedbackDialogOpen();
          setFeedbackMsg(data.message);
        }
      })
      .catch(err => {
        setDianTriggering(false);
        handleFeedbackDialogOpen();
        setFeedbackMsg(err.message);
      });
  };

  // Endpoint 2: Lectura en Outlook, PDF, ZIP y envío definitivo al cliente (Mantiene /api/send-dian-emails)
  const handleProcessAndSendFinalEmails = () => {
    const confirmSend = window.confirm(`¿Desea leer los correos en Outlook, reconstruir el ZIP con el nuevo PDF y enviar a la lista de ${props.username}?`);
    if (!confirmSend) return;

    setEmailProcessing(true);

    authFetch(`/api/send-dian-emails?username=${String(props.username)}` +
      `&year=${billingDate.getFullYear()}&month=${String(billingDate.getMonth() + 1)}` +
      `&cycle=${selectedCycle}&version=${props.tariffCalcVersion}`)
      .then(response => {
        if (response.status === 422) { logout(); return null; }
        else if (response.status !== 200) {
          return response.json().then(err => { throw new Error(err.error || "Error al procesar y reenviar correos"); });
        }
        return response.json();
      })
      .then(data => {
        setEmailProcessing(false);
        if (data && data.status === "OK") {
          handleFeedbackDialogOpen();
          setFeedbackMsg(data.message);
        }
      })
      .catch(err => {
        setEmailProcessing(false);
        handleFeedbackDialogOpen();
        setFeedbackMsg(err.message);
      });
  };
const handleDianIssuance = () => {
    setDianIssuing(true);

    authFetch(
      `/api/run-dian-issuance?username=${String(props.username)}` +
      `&year=${billingDate.getFullYear()}&month=${String(billingDate.getMonth() + 1)}` +
      `&cycle=${selectedCycle}&version=${props.tariffCalcVersion}`
    )
      .then(async (response) => {
        if (response.status === 422) {
          logout();
          return null;
        }

        // 1. Parsear el body primero de forma asíncrona y segura
        const data = await response.json().catch(() => ({}));

        // 2. Si el código HTTP no es 200/2xx
        if (!response.ok) {
          const errorDetail = data?.error || data?.message || `Código HTTP ${response.status}`;
          handleFeedbackDialogOpen();
          setFeedbackMsg(`Error al ejecutar la emisión ante la DIAN: ${errorDetail}`);
          return null;
        }

        return data;
      })
      .then((data) => {
        if (!data) return; // Si ya se manejó el 422 o un error HTTP previo

        handleFeedbackDialogOpen();
        if (data.status === "OK") {
          setFeedbackMsg(data.message || "Emisión DIAN completada exitosamente.");
        } else {
          const errorDetail = data.error || data.message || "Respuesta no esperada del servidor.";
          setFeedbackMsg(`Error en la emisión DIAN: ${errorDetail}`);
        }
      })
      .catch((err) => {
        handleFeedbackDialogOpen();
        setFeedbackMsg("Error de conexión al emitir las facturas con la DIAN.");
      })
      .finally(() => {
        // Garantiza que el spinner/estado de carga se desactive siempre
        setDianIssuing(false);
      });
  };

  const inputsStateList = (files, filesState) => (
    <Fragment>
      <Grid container justify="center">
        <Grid item xs={12}>
          <Typography variant="subtitle2" className={classes.componentTitle}>Liquidación y parámetros</Typography>
        </Grid>
        {files.length > 0 ? (
          <Grid container justify="center">
            <Grid item xs={11}>
              <Grid container justify="flex-start">
                <Grid item xs={7} style={{ fontWeight: 'bold' }}>
                  Nombre
                </Grid>
                <Grid item xs={5} style={{ fontWeight: 'bold' }}>
                  Estado
                </Grid>
                {files.map((filename, idx) => (
                  <Fragment key={idx}>
                    <Grid item xs={7}>
                      {filename}
                    </Grid>
                    <Grid item xs={5}>
                      {filesState[idx] ? (
                        <Grid container alignItems="flex-start" justify="center" spacing={2}>
                          <Grid item>
                            <DoneIcon fontSize="small" color="success" />
                          </Grid>
                          <Grid item>Listo</Grid>
                        </Grid>
                      ) : (
                        <Grid container alignItems="flex-start" justify="center" spacing={2}>
                          <Grid item>
                            <HighlightOffIcon fontSize="small" color="error" />
                          </Grid>
                          <Grid item>Pendiente</Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Fragment>
                ))}
              </Grid>
            </Grid>
          </Grid>
        ) : null}
      </Grid>
    </Fragment>
  );

  const callBillingGeneration = (withHes = false) => {
    const currentType = withHes ? "hes" : "estandar";
    setGenerationTypeInProgress(currentType);
    setGenerationMsg(withHes ? "Procesando facturas con HES... " : "Procesando facturas estándar... ");

    authFetch("/api/generate-cycle-consumption-images?username=" + String(props.username) +
      "&year=" + billingDate.getFullYear() + "&month=" + String(billingDate.getMonth() + 1) +
      "&cycle=" + selectedCycle + "&version=" + props.tariffCalcVersion).then(response => {
        if (response.status === 422) { logout(); return null; }
        else if (response.status !== 200) {
          handleFeedbackDialogOpen(); setFeedbackMsg("Error en el proceso.");
          setGenerationTypeInProgress("");
          return null;
        }
        return response.json();
      }).then(response => {
        if (response && response.Upload === 'OK') {
          setGenerationMsg("Construyendo archivo... ");

          authFetch(`/api/generate-cycle-billing?username=${String(props.username)}&year=${billingDate.getFullYear()}&month=${String(billingDate.getMonth() + 1)}&cycle=${selectedCycle}&version=${props.tariffCalcVersion}&with_hes=${withHes}`).then(response => {
            if (response.status === 422) { logout(); return null; }
            else if (response.status !== 200) {
              handleFeedbackDialogOpen(); setFeedbackMsg("Error en el proceso."); setGenerationMsg("");
              setGenerationTypeInProgress("");
              return null;
            }
            return response.json();
          }).then(response => {
            if (response && response.Upload === 'OK') {
              handleFeedbackDialogOpen();
              setFeedbackMsg(withHes ? "Facturas con HES generadas." : "Facturas estándar generadas.");
              setGenerationMsg("");
              setGenerationTypeInProgress("");
            } else {
              handleFeedbackDialogOpen(); setFeedbackMsg("Error en el proceso."); setGenerationMsg("");
              setGenerationTypeInProgress("");
            }
          });
        } else {
          setGenerationTypeInProgress("");
        }
      });
  };

  const downloadBilling = (withHes = false) => {
    const currentType = withHes ? "hes" : "estandar";
    setDownloadTypeInProgress(currentType);

    authFetch("/api/download-cycle-billing-file?username=" + String(props.username) +
      "&year=" + billingDate.getFullYear() + "&month=" + String(billingDate.getMonth() + 1) +
      "&cycle=" + selectedCycle + "&timestamp=" + new Date().toISOString().slice(0, 19)
      + `&version=${props.tariffCalcVersion}`
      + `&with_hes=${withHes}`, { responseType: 'blob' }).then(response => {
        if (response.status === 422) {
          logout();
          return null;
        }
        else if (response.status !== 200) {
          handleFeedbackDialogOpen();
          setFeedbackMsg("Error en el proceso.");
          setDownloadTypeInProgress("");
          return null;
        }
        return response.blob();
      }).then(blob => {
        setDownloadTypeInProgress("");
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;

        const suffix = (props.username === "Cedenar" && withHes) ? '_HES' : '_SIN_HES';
        a.download = 'Facturas_' + props.username + '_' + billingDate.getFullYear() + '_' + String(billingDate.getMonth() + 1) +
          '_' + 'Ciclo' + '_' + selectedCycle + suffix + '.pdf';

        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }).catch(err => {
        setDownloadTypeInProgress("");
      });
  };

  const showFeedbackDialog = () => (
    <Dialog
      open={feedbackDialog}
      onClose={handleFeedbackDialogClose}
      className={classes.formText}
    >
      <DialogTitle className={classes.dialogTitle} style={{ textAlign: 'center' }}>Aviso</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <DialogContentText>
          {feedbackMsg}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleFeedbackDialogClose} color="primary" autoFocus>
          Aceptar
        </Button>
      </DialogActions>
    </Dialog>
  );

  const updateFlags = (value) => {
    setCycleLiqAgreementFlag(cycleLiqAgreement[value]);
    setCycleLiqConsumptionFlag(cycleLiqConsumption[value]);
    setCycleBillParamsFlag(cycleBillParams[value]);
    setCycleBillGeneratedFlag(cycleBillGenerated[value]);
    setCycleHesFlag(cycleHes[value]);
    setCycleBillGeneratedWithHesFlag(cycleBillGeneratedWithHes[value]);
  };

  useEffect(() => {
    document.title = "Generar facturas - Legacy | Facturador";

    authFetch("/api/check-billing-cycles?username=" + String(props.username)).then(response => {
      if (response.status === 422) { logout(); return null; }
      return response.json();
    }).then(response => {
      if (response && response.cycles) { setCycles(response.cycles); }
    });

    authFetch("/api/get-cycle-users?username=" + String(props.username) + "&billing=true").then(response => {
      if (response.status === 422) { logout(); return null; }
      return response.json();
    }).then(response => {
      if (response && response.cycle_users) {
        let cycleUsers = response.cycle_users;
        setCycleUsers(response.cycle_users);
        let opts = {
          users_by_cycle: cycleUsers,
          year: billingDate.getFullYear(),
          month: String(billingDate.getMonth() + 1)
        };
        authFetch("/api/check-billing-generation-params?username=" + String(props.username) +
          "&year=" + billingDate.getFullYear() + "&month=" + String(billingDate.getMonth() + 1) + "&version=" + props.tariffCalcVersion,
          {
            method: 'post',
            body: JSON.stringify(opts)
          }
        ).then(response => {
          if (response.status === 422) { logout(); return null; }
          return response.json();
        }).then(response => {
          if (response && response.params_flags) {
            setCycleLiqConsumption(response.consumption_flags);
            setCycleLiqAgreement(response.munip_tax_flags);
            setCycleBillParams(response.params_flags);
            setCycleBillGenerated(response.bill_generated_flags);
            setCycleHes(response.hes_flags);
            setCycleBillGeneratedWithHes(response.bill_generated_with_hes_flags);

            if (selectedCycle !== "") {
              setCycleLiqAgreementFlag(response.munip_tax_flags[selectedCycle]);
              setCycleLiqConsumptionFlag(response.consumption_flags[selectedCycle]);
              setCycleBillParamsFlag(response.params_flags[selectedCycle]);
              setCycleBillGeneratedFlag(response.bill_generated_flags[selectedCycle]);
              setCycleHesFlag(response.hes_flags[selectedCycle]);
              setCycleBillGeneratedWithHesFlag(response.bill_generated_with_hes_flags[selectedCycle]);
            }
          }
        });
      }
    });
  }, [props.billingDate, selectedCycle, generationTypeInProgress]);

  const anyLoading = generationTypeInProgress !== "" || downloadTypeInProgress !== "" || dianValidating || dianIssuing || dianTriggering || emailProcessing;

  return (
    <div className={classes.root}>
      {showFeedbackDialog()}
      <Zoom in timeout={800}>
        <Grid container justify="space-evenly">
          <Grid item xs={12} style={{ marginTop: 10 }}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>Generar facturas por ciclo</Typography>
            <Grid container justify="space-evenly" spacing={1} alignItems="flex-start">
              <Grid item xs={11}>
                <Typography variant="subtitle2" align="center" color="primary">Ciclo:</Typography>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  select
                  variant="outlined"
                  size="small"
                  InputProps={{
                    classes: {
                      input: classes.formText
                    }
                  }}
                  className={classes.formRow}
                  value={selectedCycle}
                  onChange={(e) => { setSelectedCycle(e.target.value); updateFlags(e.target.value); }}
                >
                  {cycles.map((cycle, idx) => (
                    cycleUsers[cycle.code] > 0 ?
                      <MenuItem key={idx} value={cycle.code}>{cycle.code}</MenuItem>
                      : null
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}></Grid>
              <Grid item xs={6} className={classes.darkText}>
                <Typography variant="subtitle2" className={classes.sectionTitle}>Insumos necesarios</Typography>
                {cycles.length > 0 && selectedCycle !== "" ?
                  inputsStateList(
                    props.username === "Cedenar"
                      ? [
                        'Liquidación aprobada consumos ciclo ' + selectedCycle,
                        'Liquidación aprobada impuestos ciclo ' + selectedCycle,
                        'Parametrización mensual facturas ciclo ' + selectedCycle,
                        'Autorización Contable (HES) ciclo ' + selectedCycle
                      ]
                      : [
                        'Liquidación aprobada consumos ciclo ' + selectedCycle,
                        'Liquidación aprobada impuestos ciclo ' + selectedCycle,
                        'Parametrización mensual facturas ciclo ' + selectedCycle
                      ],
                    props.username === "Cedenar"
                      ? [cycleLiqConsumptionFlag, cycleLiqAgreementFlag, cycleBillParamsFlag, cycleHesFlag]
                      : [cycleLiqConsumptionFlag, cycleLiqAgreementFlag, cycleBillParamsFlag]
                  )
                  : null
                }
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" className={classes.sectionTitle}>Control de aprobación</Typography>

                {props.username === "Cedenar" ? (
                  <Grid container direction="column" spacing={2} style={{ marginTop: 20 }}>

                    {/* Fila 1: Facturas Estándar (Sin HES) */}
                    <Grid item>
                      <Typography variant="caption" color="textSecondary" style={{ fontWeight: 'bold' }}>FACTURACIÓN ESTÁNDAR (SIN HES)</Typography>
                      <Grid container spacing={2} style={{ marginTop: 5 }} justify="space-evenly">
                        <Grid item>
                          {generationTypeInProgress === "estandar" ? <CircularProgress size={24} /> : (
                            <Button
                              onClick={(e) => { callBillingGeneration(false); }}
                              color='primary'
                              disabled={generationTypeInProgress !== "" || !cycleLiqConsumptionFlag || !cycleLiqAgreementFlag || !cycleBillParamsFlag || cycleBillGeneratedFlag}
                              variant='contained'
                              size="small"
                            >
                              Generar Estándar
                            </Button>
                          )}
                        </Grid>
                        <Grid item>
                          {downloadTypeInProgress === "estandar" ? <CircularProgress size={24} /> : (
                            <Button
                              onClick={(e) => { downloadBilling(false); }}
                              color='primary'
                              disabled={downloadTypeInProgress !== "" || !cycleBillGeneratedFlag}
                              variant='contained'
                              size="small"
                            >
                              Descargar Estándar
                            </Button>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Botonera de acciones DIAN */}
                    <Grid item>
                      <Grid container spacing={1} justify="center" alignItems="center">
                        <Grid item>
                          {dianValidating ? (
                            <CircularProgress size={24} style={{ marginRight: 10 }} />
                          ) : (
                            <Button
                              onClick={handleDianValidation}
                              color='default'
                              disabled={selectedCycle === "" || anyLoading}
                              variant='contained'
                              style={{ backgroundColor: '#4caf50', color: '#fff', fontSize: 13 }}
                              size="small"
                            >
                              Validar DIAN
                            </Button>
                          )}
                        </Grid>
                        <Grid item>
                          {dianIssuing ? (
                            <CircularProgress size={24} style={{ marginRight: 10 }} />
                          ) : (
                            <Button
                              onClick={handleDianIssuance}
                              color='secondary'
                              disabled={selectedCycle === "" || anyLoading}
                              variant='contained'
                              style={{ backgroundColor: '#d32f2f', color: '#fff', fontSize: 13 }}
                              size="small"
                            >
                              Emitir DIAN
                            </Button>
                          )}
                        </Grid>
                        <Grid item>
                          {dianTriggering ? (
                            <CircularProgress size={24} style={{ marginRight: 10 }} />
                          ) : (
                            <Button
                              onClick={handleTriggerDianEmails}
                              color='default'
                              disabled={selectedCycle === "" || anyLoading}
                              variant='contained'
                              style={{ backgroundColor: '#ff9800', color: '#fff', fontSize: 13 }}
                              size="small"
                            >
                              Disparar a Outlook
                            </Button>
                          )}
                        </Grid>
                        <Grid item>
                          {emailProcessing ? (
                            <CircularProgress size={24} style={{ marginRight: 10 }} />
                          ) : (
                            <Button
                              onClick={handleProcessAndSendFinalEmails}
                              color='default'
                              disabled={selectedCycle === "" || anyLoading}
                              variant='contained'
                              style={{ backgroundColor: '#0288d1', color: '#fff', fontSize: 13 }}
                              size="small"
                            >
                              Procesar y Enviar
                            </Button>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Fila 2: Facturas Especiales (Con HES) */}
                    <Grid item style={{ marginTop: 15 }}>
                      <Typography variant="caption" color="textSecondary" style={{ fontWeight: 'bold' }}>FACTURACIÓN CON AUTORIZACIÓN (HES)</Typography>
                      <Grid container spacing={2} style={{ marginTop: 5 }} justify="space-evenly">
                        <Grid item>
                          {generationTypeInProgress === "hes" ? <CircularProgress size={24} /> : (
                            <Button
                              onClick={(e) => { callBillingGeneration(true); }}
                              color='secondary'
                              disabled={generationTypeInProgress !== "" || !cycleLiqConsumptionFlag || !cycleLiqAgreementFlag || !cycleBillParamsFlag || !cycleHesFlag || cycleBillGeneratedWithHesFlag}
                              variant='contained'
                              size="small"
                            >
                              Generar Con HES
                            </Button>
                          )}
                        </Grid>
                        <Grid item>
                          {downloadTypeInProgress === "hes" ? <CircularProgress size={24} /> : (
                            <Button
                              onClick={(e) => { downloadBilling(true); }}
                              color='secondary'
                              disabled={downloadTypeInProgress !== "" || !cycleBillGeneratedWithHesFlag}
                              variant='contained'
                              size="small"
                            >
                              Descargar Con HES
                            </Button>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  /* VISTA TRADICIONAL OPERADORES estándar */
                  <Grid container justify="space-evenly" spacing={1} style={{ marginTop: 40 }}>
                    <Grid item>
                      {generationTypeInProgress === "estandar" ? (
                        <Grid container spacing={2} justify="center">
                          <CircularProgress />
                          <Grid item xs={12}></Grid>
                          <Typography variant="subtitle2" color="primary">{generationMsg}</Typography>
                        </Grid>
                      ) : (
                        <Button
                          onClick={(e) => { callBillingGeneration(false); }}
                          color='primary'
                          disabled={generationTypeInProgress !== "" || !cycleLiqConsumptionFlag || !cycleLiqAgreementFlag || !cycleBillParamsFlag || cycleBillGeneratedFlag}
                          variant='contained'
                        >
                          Generar
                        </Button>
                      )}
                    </Grid>
                    <Grid item>
                      {downloadTypeInProgress === "estandar" ? <CircularProgress /> : (
                        <Button
                          onClick={(e) => { downloadBilling(false); }}
                          color='primary'
                          disabled={downloadTypeInProgress !== "" || !cycleBillGeneratedFlag}
                          variant='contained'
                        >
                          Descargar
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Zoom>
    </div>
  );
}